import React, {useEffect, useMemo, useState} from "react";
import {ActionIcon, Box, Button, Group, Stack, Text, TextInput} from '@mantine/core';
import {DataTable, DataTableSortStatus} from "mantine-datatable";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {
    ApiError,
    NewsletterService,
    PasskeyService,
    Subscriber,
    UserService,
    type app__schema__subscriber__Add,
} from "../../client";
import {startAuthentication, type PublicKeyCredentialRequestOptionsJSON} from "@simplewebauthn/browser";
import {PageLayout} from '../shell/PageLayout';
import {DeleteTableAction} from '../common/TableActionIcons';
import sortBy from "lodash/sortBy";
import {closeModal, openModal} from "@mantine/modals";
import {useDebouncedValue} from "@mantine/hooks";
import {IconSearch, IconX} from "@tabler/icons-react";
import {notifications} from "@mantine/notifications";
import {useAuthStore} from "../../store/authStore";
import {jwtDecode} from "jwt-decode";
import {formatRequestError} from "../../utils/formatRequestError";
import {
    largeEditorModalBodyStyle,
    largeEditorModalProps,
    largeEditorModalScrollAreaStyle,
} from '../../utils/largeEditorModal';
import {useDataTablePagination} from '../../utils/useDataTablePagination';
import {NewsletterEditor} from './NewsletterEditor';
import {NewsletterSendProgress} from './NewsletterSendProgress';
import {canManageNewsletter, normalizeRole} from '../../utils/roles';

const passkeySupported = typeof window !== 'undefined' && !!window.PublicKeyCredential;

type SendNewsletterConfirmationProps = {
    username: string;
    hasPasskey: boolean;
};

function SendNewsletterConfirmation({username, hasPasskey}: SendNewsletterConfirmationProps) {
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const queryClient = useQueryClient();

    const finishSend = () => NewsletterService.sendNewsletterToSubscribersApiV1NewsletterPost()
        .then(() => {
            queryClient.invalidateQueries({queryKey: ['newsletter-send-status']});
            notifications.show({
                color: 'green',
                title: 'Newsletter sent',
                message: 'The newsletter is being sent to all subscribers.',
            });
            closeModal('sendNewsletter');
            closeModal('sendNewsletterEditor');
        })
        .catch((error: unknown) => {
            notifications.show({
                color: error instanceof ApiError && error.status === 409 ? 'yellow' : 'red',
                title: error instanceof ApiError && error.status === 409
                    ? 'Send already in progress'
                    : 'Could not send newsletter',
                message: formatRequestError(error, 'Request failed'),
            });
        });

    const sendWithPassword = () => {
        if (!username || !password) {
            return;
        }
        setLoading(true);
        UserService.loginForAccessTokenApiV1UserLoginPost({
            formData: {username, password},
        })
            .then(() => finishSend())
            .catch((error: unknown) => {
                if (error instanceof ApiError && error.status === 409) {
                    return;
                }
                notifications.show({
                    color: 'red',
                    title: 'Could not send newsletter',
                    message: formatRequestError(error, 'Request failed'),
                });
            })
            .finally(() => setLoading(false));
    };

    const sendWithPasskey = async () => {
        if (!username) {
            return;
        }
        if (!passkeySupported) {
            notifications.show({
                color: 'yellow',
                title: 'Passkey unavailable',
                message: 'Passkeys require HTTPS (e.g. mkcert for local development).',
            });
            return;
        }
        setLoading(true);
        try {
            const optionsResponse = await PasskeyService.passkeyLoginOptionsApiV1PasskeyLoginOptionsPost({
                username: username.trim(),
            });
            if (!optionsResponse.has_passkey || !optionsResponse.options || !optionsResponse.challenge_token) {
                notifications.show({
                    color: 'red',
                    title: 'Could not send newsletter',
                    message: 'No passkey registered for this user.',
                });
                return;
            }
            const credential = await startAuthentication({
                optionsJSON: optionsResponse.options as unknown as PublicKeyCredentialRequestOptionsJSON,
            });
            await PasskeyService.passkeyLoginVerifyApiV1PasskeyLoginVerifyPost({
                credential: credential as unknown as Record<string, unknown>,
                challenge_token: optionsResponse.challenge_token,
                username: username.trim(),
            });
            await finishSend();
        } catch (error: unknown) {
            notifications.show({
                color: 'red',
                title: 'Could not send newsletter',
                message: formatRequestError(error, 'Authentication was cancelled or failed.'),
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Stack gap="xs">
            <Text size="sm">
                This will send the saved newsletter to all subscribers.{' '}
                {hasPasskey
                    ? 'Confirm with your passkey to continue.'
                    : 'Enter your password to confirm.'}
            </Text>
            {hasPasskey ? (
                <>
                    {!passkeySupported ? (
                        <Text size="sm" c="dimmed">
                            Passkeys require HTTPS (e.g. mkcert for local development).
                        </Text>
                    ) : null}
                    <Group gap="xs">
                        <Button
                            loading={loading}
                            disabled={!passkeySupported}
                            onClick={() => void sendWithPasskey()}
                        >
                            Confirm with passkey
                        </Button>
                        <Button variant="default" onClick={() => closeModal('sendNewsletter')}>Cancel</Button>
                    </Group>
                </>
            ) : (
                <>
                    <TextInput
                        label="Password"
                        type="password"
                        required
                        placeholder="Your password"
                        value={password}
                        onChange={(e) => setPassword(e.currentTarget.value)}
                    />
                    <Group gap="xs">
                        <Button loading={loading} onClick={sendWithPassword}>Send newsletter</Button>
                        <Button variant="default" onClick={() => closeModal('sendNewsletter')}>Cancel</Button>
                    </Group>
                </>
            )}
        </Stack>
    );
}

type SendNewsletterEditorModalProps = {
    username: string;
    hasPasskey: boolean;
    sendInProgress: boolean;
};

function SendNewsletterEditorModal({
    username,
    hasPasskey,
    sendInProgress,
}: SendNewsletterEditorModalProps) {
    const showConfirmationModal = () => {
        if (sendInProgress) {
            notifications.show({
                color: 'yellow',
                title: 'Send already in progress',
                message: 'A newsletter is currently being sent to all subscribers.',
            });
            return;
        }
        openModal({
            modalId: 'sendNewsletter',
            title: 'Send newsletter',
            children: (
                <SendNewsletterConfirmation
                    username={username}
                    hasPasskey={hasPasskey}
                />
            ),
        });
    };

    return (
        <Stack gap="md" style={largeEditorModalBodyStyle}>
            <Box style={largeEditorModalScrollAreaStyle}>
                <NewsletterEditor />
            </Box>
            <Group gap="xs" justify="flex-end">
                <Button variant="default" onClick={() => closeModal('sendNewsletterEditor')}>
                    Cancel
                </Button>
                <Button
                    color="red"
                    variant="outline"
                    onClick={showConfirmationModal}
                    disabled={sendInProgress}
                >
                    Send newsletter
                </Button>
            </Group>
        </Stack>
    );
}

function NewsletterSubscribers({
    isAdmin,
    sendInProgress,
    onSendNewsletter,
}: {
    isAdmin: boolean;
    sendInProgress: boolean;
    onSendNewsletter: () => void;
}) {
    const {isLoading, data} = useQuery({
        queryKey: ['subscribers'],
        queryFn: NewsletterService.getAllSubscribersApiV1NewsletterSubscribersGet,
    });
    const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
    const [sortStatus, setSortStatus] = useState<DataTableSortStatus<Subscriber>>({
        columnAccessor: 'firstname',
        direction: 'asc',
    });

    const {page, recordsPerPage, paginate, getPaginationProps} = useDataTablePagination();
    const [records, setRecords] = useState<Subscriber[]>([]);
    const [query, setQuery] = useState('');
    const [debouncedQuery] = useDebouncedValue(query, 200);
    useEffect(() => {
        const sorted = (sortBy(data ?? [], sortStatus.columnAccessor) as Subscriber[]).filter((subscriber: Subscriber) => {
            if (debouncedQuery === '') {
                return true;
            }
            const lowercase: string = debouncedQuery.toLowerCase();
            return subscriber.email.toLowerCase().includes(lowercase);
        })
        const tmp = sortStatus.direction === 'desc' ? sorted.reverse() : sorted;
        setSubscribers(tmp);
        setRecords(paginate(tmp));
    }, [debouncedQuery, sortStatus, data, page, recordsPerPage, paginate]);

    const queryClient = useQueryClient();

    const removeSubscriber = (subscriber: Subscriber) => {
        NewsletterService.deleteSubscriberByEmailApiV1NewsletterSubscriberEmailDelete({
            email: subscriber.email,
        })
            .then(() => {
                queryClient.invalidateQueries({queryKey: ['subscribers']});
                closeModal('delete');
            })
            .catch((error: { body?: { detail?: string }; message?: string }) => {
                notifications.show({
                    color: 'red',
                    title: 'Could not delete subscriber',
                    message: error.body?.detail ?? error.message ?? 'Request failed',
                });
            });
    };

    const addSubscriber = (subscriber: app__schema__subscriber__Add) => {
        if (!subscriber.email || !subscriber.firstname) {
            return;
        }
        NewsletterService.addSubscriberApiV1NewsletterSubscriberPost({
            requestBody: subscriber,
        })
            .then(() => {
                queryClient.invalidateQueries({queryKey: ['subscribers']});
                closeModal('addSubscriber');
            })
            .catch((error: { body?: { detail?: string }; message?: string }) => {
                notifications.show({
                    color: 'red',
                    title: 'Could not add subscriber',
                    message: error.body?.detail ?? error.message ?? 'Request failed',
                });
            });
    };

    const showAddModal = () => {
        const subscriber: app__schema__subscriber__Add = {email: '', firstname: ''};
        openModal({
            modalId: 'addSubscriber',
            title: 'Add subscriber',
            children: (
                <>
                    <TextInput
                        label="First name"
                        required
                        placeholder="First name"
                        onChange={(e) => {
                            subscriber.firstname = e.target.value;
                        }}
                    />
                    <TextInput
                        label="E-mail"
                        required
                        placeholder="e@mail.com"
                        onChange={(e) => {
                            subscriber.email = e.target.value;
                        }}
                    />
                    <Button type="submit" variant="outline" mt="xs" onClick={() => addSubscriber(subscriber)}>
                        Add
                    </Button>
                </>
            ),
        });
    };

    const showModal = ({subscriber, action}: {subscriber: Subscriber, action: 'delete'}) => {
        openModal({
            modalId: action,
            title: 'Delete subscriber',
            children: (
                <>
                    <p>Are you sure you want to delete <strong>{subscriber.firstname}</strong>?</p>
                    <Stack gap="xs">
                        <Button color="red" onClick={() => removeSubscriber(subscriber)}>Yes</Button>
                        <Button onClick={() => closeModal(action)}>No</Button>
                    </Stack>
                </>
            )
        })
    };

    return (
        <Stack gap="md">
            <Group gap="xs">
                <Button variant="outline" onClick={showAddModal}>
                    Add subscriber
                </Button>
                {isAdmin ? (
                    <Button
                        color="red"
                        variant="outline"
                        onClick={onSendNewsletter}
                        disabled={sendInProgress}
                    >
                        Send newsletter
                    </Button>
                ) : null}
            </Group>
            <DataTable
                withTableBorder
                striped
                highlightOnHover
                records={records}
                fetching={isLoading}
                loaderType='oval'
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                {...getPaginationProps(subscribers.length)}
                columns={[
                    {accessor: 'firstname', title: 'Firstname', sortable: true},
                    {accessor: 'email', title: 'Email',
                        sortable: true,
                        filter:(
                            <TextInput
                                label="Search"
                                description="Show emails which include"
                                placeholder="Search emails..."
                                leftSection={<IconSearch size={16} />}
                                rightSection={
                                    <ActionIcon size="sm" variant="transparent" c="dimmed" onClick={() => setQuery('')}>
                                        <IconX size={14} />
                                    </ActionIcon>
                                }
                                value={query}
                                onChange={(e) => setQuery(e.currentTarget.value)}
                            />),
                        filtering: query !== '',},
                    {
                        accessor: 'actions',
                        title: '',
                        render: (record) => (
                            <DeleteTableAction
                                onClick={() => showModal({subscriber: record, action: 'delete'})}
                                label="Delete subscriber"
                            />
                        ),
                    },
                ]}
            />
        </Stack>
    );
}

export const Newsletter = () => {
    const token = useAuthStore(state => state.token);
    const username = useMemo(() => {
        if (!token) {
            return '';
        }
        try {
            return jwtDecode<{sub?: string}>(token).sub ?? '';
        } catch {
            return '';
        }
    }, [token]);

    const {data: currentUserProfile} = useQuery({
        queryKey: ['current-user'],
        queryFn: UserService.getCurrentUserApiV1UserMeGet,
    });
    const canManageNewsletterAccess = canManageNewsletter(
        normalizeRole(currentUserProfile?.role),
    );

    const {data: passkeyStatus} = useQuery({
        queryKey: ['passkey-status'],
        queryFn: PasskeyService.passkeyStatusApiV1PasskeyStatusGet,
    });

    const {data: sendStatus} = useQuery({
        queryKey: ['newsletter-send-status'],
        queryFn: NewsletterService.getNewsletterSendStatusApiV1NewsletterSendStatusGet,
        refetchInterval: (query) => (query.state.data?.sending ? 5000 : false),
    });

    const showSendNewsletterModal = () => {
        if (sendStatus?.sending) {
            notifications.show({
                color: 'yellow',
                title: 'Send already in progress',
                message: 'A newsletter is currently being sent to all subscribers.',
            });
            return;
        }
        openModal({
            modalId: 'sendNewsletterEditor',
            title: 'Send newsletter',
            ...largeEditorModalProps,
            children: (
                <SendNewsletterEditorModal
                    username={username}
                    hasPasskey={passkeyStatus?.has_passkey ?? false}
                    sendInProgress={sendStatus?.sending ?? false}
                />
            ),
        });
    };

    return (
        <PageLayout
            title="Newsletter"
            description="Manage subscribers and send mailings."
        >
            <Stack gap="md">
                <NewsletterSendProgress status={sendStatus} isAdmin={canManageNewsletterAccess} />
                <NewsletterSubscribers
                    isAdmin={canManageNewsletterAccess}
                    sendInProgress={sendStatus?.sending ?? false}
                    onSendNewsletter={showSendNewsletterModal}
                />
            </Stack>
        </PageLayout>
    );
}
